// src/api/features/dashboard/queries.ts
import {
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { getAdminDashboardSummary } from "./api";
import { DashboardKeys } from "./types";

/* =========================
 * React Query Hooks
 * ========================= */

/**
 * 🔹 Fetch admin dashboard summary (monthly).
 *
 * Backend side:
 * - Requires auth + admin role.
 * - Query params (optional): month (1..12), year (2000..2100)
 */
export function useAdminDashboardSummary(params?: {
  month?: number;
  year?: number;
  employeeId?: number;
}, options?: { enabled?: boolean }) {
  const key: QueryKey = DashboardKeys.adminSummary(
    params?.month,
    params?.year,
    params?.employeeId
  );

  return useQuery({
    queryKey: key,
    queryFn: () => getAdminDashboardSummary(params),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,        // 1 min — dashboard can be slightly stale
    gcTime: 10 * 60_000,      // 10 min cache
  });
}

/* =========================
 * Prefetch helper (optional)
 * ========================= */

export function prefetchAdminDashboardSummary(
  qc: ReturnType<typeof useQueryClient>,
  params?: {
    month?: number;
    year?: number;
    employeeId?: number;
  }
): Promise<void> {
  return qc.prefetchQuery({
    queryKey: DashboardKeys.adminSummary(
      params?.month,
      params?.year,
      params?.employeeId
    ),
    queryFn: () => getAdminDashboardSummary(params),
    staleTime: 60_000,
  }) as Promise<void>;
}
